export type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg';

interface SpinnerProps {
  /** xs 14px, sm 16px (the default), md 24px, lg 32px. */
  size?: SpinnerSize;
  className?: string;
}

// Each size is written out whole: Tailwind only emits a class it can read in the source.
// The border's width is part of the size, never added to it. Two border-width classes on
// one element are settled by the order Tailwind emits them, not the order written.
const SIZES: Record<SpinnerSize, { border: string; box: string }> = {
  xs: { border: 'border-2', box: 'h-3.5 w-3.5' },
  sm: { border: 'border-2', box: 'h-4 w-4' },
  md: { border: 'border-2', box: 'h-6 w-6' },
  lg: { border: 'border-[3px]', box: 'h-8 w-8' },
};

/**
 * The ring: a pale track with a head in the project's theme colour, turning.
 *
 * It says nothing on its own (aria-hidden): put it beside words, or use InlineLoader or
 * BlockLoader, which carry the words and the role. It never shrinks in a flex row, and
 * it stands still for a person who has asked their device for less motion.
 */
export function Spinner({ size = 'sm', className = '' }: SpinnerProps) {
  const { border, box } = SIZES[size];
  return (
    <span
      aria-hidden="true"
      className={`inline-block rounded-full ${border} border-primary-100 border-t-primary-600 animate-spin motion-reduce:animate-none shrink-0 ${box} ${className}`.trim()}
    />
  );
}
